import { useLazyQuery, useMutation } from "@apollo/client";
import { omit } from "lodash";
import { useCallback } from "react";
import {
  AssessmentType,
  GetComplianceProgramReportingViewsQuery,
  GetComplianceProgramReportingViewsQueryVariables,
  SaveComplianceProgramReportingViewMutation,
  SaveComplianceProgramReportingViewMutationVariables,
  TrashComplianceProgramReportingViewMutation,
  TrashComplianceProgramReportingViewMutationVariables,
} from "types/graphql-schema";

import createOrUpdateComplianceProgramReportingViewMutation from "@apps/reporting/graphql/create-or-update-compliance-program-reporting-view-mutation";
import deleteComplianceProgramReportingViewMutation from "@apps/reporting/graphql/delete-compliance-program-reporting-view-mutation";
import getComplianceProgramReportingViews from "@apps/reporting/graphql/get-compliance-program-reporting-views";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import { assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import { BaseReportViewOptions } from "../assessment-report/report-filters/types";

const useReportingViews = <T extends BaseReportViewOptions>({
  complianceProgramId,
  assessmentType,
  viewOptions,
  baselineViewOptions,
  onResetViewOptionsToBaseline,
  onUpdateViewOptions,
  onUpdateBaselineViewOptions,
  onResetViewOptionsToEmpty,
}: {
  complianceProgramId: number;
  assessmentType: AssessmentType;
  viewOptions: T;
  baselineViewOptions: T;
  onResetViewOptionsToBaseline: () => void;
  onUpdateViewOptions: (opts: Partial<T>) => void;
  onUpdateBaselineViewOptions: (opts: T) => void;
  onResetViewOptionsToEmpty: () => void;
}) => {
  const [getReportingViews, { data: reportingViewsData }] = useLazyQuery<
    GetComplianceProgramReportingViewsQuery,
    GetComplianceProgramReportingViewsQueryVariables
  >(getComplianceProgramReportingViews);
  const [saveReportingView, { loading: isSavingReportingView }] = useMutation<
    SaveComplianceProgramReportingViewMutation,
    SaveComplianceProgramReportingViewMutationVariables
  >(createOrUpdateComplianceProgramReportingViewMutation);
  const [deleteReportingView, { loading: isDeletingReportingView }] =
    useMutation<
      TrashComplianceProgramReportingViewMutation,
      TrashComplianceProgramReportingViewMutationVariables
    >(deleteComplianceProgramReportingViewMutation);

  const handleSaveReportingView = useCallback(
    (opts: T) => {
      saveReportingView({
        variables: {
          complianceProgramId,
          assessmentType,
          viewOptions: JSON.stringify(omit(opts, "id", "name", "isDirty")),
          id: opts.id,
          name: opts.name,
        },
        refetchQueries: [getComplianceProgramReportingViews],
        onCompleted: (response) => {
          const newViewOptionsData = assertNonNull(
            response.createOrUpdateComplianceProgramReportingView
              ?.complianceProgramReportingView,
          );
          onResetViewOptionsToBaseline();
          try {
            const newViewOptions = {
              id: newViewOptionsData?.id,
              name: newViewOptionsData?.name,
              isDirty: false,
              ...JSON.parse(newViewOptionsData?.viewOptions),
            };
            onUpdateViewOptions(newViewOptions);
            onUpdateBaselineViewOptions(newViewOptions);

            successNotificationVar({
              title: "View saved successfully",
              timeout: 2000,
            });
          } catch {
            errorNotificationVar({
              title: "Failed to parse view options",
            });
          }
        },
      });
    },
    [
      assessmentType,
      complianceProgramId,
      onResetViewOptionsToBaseline,
      onUpdateBaselineViewOptions,
      onUpdateViewOptions,
      saveReportingView,
    ],
  );

  const handleDuplicateView = useCallback(() => {
    handleSaveReportingView({
      ...baselineViewOptions,
      id: undefined,
      name:
        viewOptions.name === baselineViewOptions.name
          ? `Copy of ${viewOptions.name}`
          : viewOptions.name.trim() === ""
            ? "New View"
            : viewOptions.name,
    });
  }, [baselineViewOptions, handleSaveReportingView, viewOptions.name]);

  const handleSaveAsNewView = useCallback(() => {
    handleSaveReportingView({
      ...viewOptions,
      id: undefined,
      name:
        viewOptions.name === baselineViewOptions.name
          ? `Copy of ${viewOptions.name}`
          : viewOptions.name.trim() === ""
            ? "New View"
            : viewOptions.name,
    });
  }, [baselineViewOptions, handleSaveReportingView, viewOptions]);

  const {
    ConfirmationDialog: ReportingViewDeletionConfirm,
    confirm: confirmDeleteReportingView,
  } = useConfirm(
    "Are you sure?",
    "Are you sure you want to delete this reporting view?",
  );

  const handleSaveExistingView = useCallback(() => {
    handleSaveReportingView({
      ...viewOptions,
      name: viewOptions.name.trim() === "" ? "New View" : viewOptions.name,
    });
  }, [handleSaveReportingView, viewOptions]);

  const handleDeleteView = useCallback(async () => {
    if (!viewOptions.id) {
      return;
    }
    const confirmation = await confirmDeleteReportingView();
    if (!confirmation) {
      return;
    }

    deleteReportingView({
      variables: {
        id: viewOptions.id,
      },
      refetchQueries: [getComplianceProgramReportingViews],
      onCompleted: () => {
        successNotificationVar({
          title: "View deleted successfully",
          timeout: 2000,
        });
        onResetViewOptionsToEmpty();
      },
    });
  }, [
    confirmDeleteReportingView,
    deleteReportingView,
    onResetViewOptionsToEmpty,
    viewOptions.id,
  ]);

  return {
    reportingViewsData,
    isSavingReportingView,
    isDeletingReportingView,
    handleSaveAsNewView,
    handleSaveExistingView,
    handleDuplicateView,
    handleDeleteView,
    ReportingViewDeletionConfirm,
    getReportingViews,
  };
};

export default useReportingViews;
